// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

import { useTranslate } from "helpers";
import { ContractRentTemplateCardProps } from "./types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

import { Pagination } from "services/search";

import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ContractRentTemplateEditCard from "./components/ContractRentTemplateEditCard/ContractRentTemplateEditCard";
import { ReactSVG } from "react-svg";
import { Dialog } from "ui/components/Dialog";

import { deleteContractRentTemplateAPI, getContractRentTemplatesAPI } from "services/api/settings/documents/api";
import { ContractRentTemplateRow } from "services/api/settings/documents/types";


const ContractRentTemplateCard: React.FC<ContractRentTemplateCardProps> = () => {

    const { t } = useTranslate();
    const [templateId, setTemplateId] = useState<string>("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<DataWithPagination<ContractRentTemplateRow>>(initialDataWithPagination);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            if (isMounted) {
                await fetchSearchResult(searchResults.pagination);
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, []);

    const fetchSearchResult = async (
        pagination: Pagination,
    ) => {
        setIsLoading(true);
        try {
            const results = await getContractRentTemplatesAPI(pagination);

            setSearchResults(results);
        } catch (error: any) {
            // Handle 404 - endpoint not found
            if (error?.response?.status === 404) {
                console.warn("Contract rent templates endpoint not available (404). This feature may not be implemented yet.");
            }
            setSearchResults(initialDataWithPagination);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddClick = () => {
        setTemplateId("");
        setShowEditModal(true);
    }

    const handleEditClick = (id: string) => {
        setTemplateId(id);
        setShowEditModal(true);
    }

    const handleTemplateEditSuccess = () => {
        fetchSearchResult(searchResults.pagination);
        setTemplateId("");
        setShowEditModal(false);
    }

    const handleDeleteClick = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
            event.stopPropagation();
            event.preventDefault();

            setTemplateId(id);
            setOpenDialog(true);
        },
        []
    );

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!templateId) return;

        try {
            const deleted = await deleteContractRentTemplateAPI(
                templateId,
                t
            );

            if (deleted) {
                await fetchSearchResult(searchResults.pagination);
            }

            // zatvori dijalog nakon brisanja
            setOpenDialog(false);
            setTemplateId("");
        } catch (err) {
            console.error("Greška prilikom brisanja predloška:", err);
        }
    }, [templateId, t, searchResults.pagination]);

    const handleDeleteDialogClose = useCallback(() => {
        setTemplateId("");
        setOpenDialog(false);
    }, []);



    return (
        <>
            {/* Predložak ugovora o najmu */}
            <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
                <div className="flex flex-col flex-1 max-w-[300px]">
                    <p className="text-base font-bold text-slate-600 py-3">
                        2.3 {t("admin.setting.document.contract.rent.template.title")}
                    </p>
                    <p className="text-sm">
                        Samostalno kreiraj i prilagodi predloške ugovora o najmu prema svojim potrebama.
                    </p>
                </div>
                <div className="flex justify-between items-center flex-1 gap-6 px-3 my-3">
                    <div className="flex flex-1 flex-col">
                        <div className="prose dark:prose-invert max-w-2xl text-sm border border-slate-200 p-4 rounded-sm bg-white mb-3">
                            <p className="mb-1">Predlošci ti omogućuju da:</p>
                            <ul className="list-disc pl-5 mb-3">
                                <li>unaprijed definiraš strukturu i sadržaj ugovora</li>
                                <li>ponovno koristiš iste ugovore bez ponovnog unosa podataka</li>
                                <li>brže i jednostavnije izrađuješ nove ugovore o najmu</li>
                            </ul>
                            <p className="mb-3">
                                Nakon što izradiš predložak, možeš ga <strong>odabrati prilikom kreiranja novog ugovora</strong>, a aplikacija će automatski popuniti osnovni sadržaj.
                            </p>
                        </div>
                        {searchResults.data?.length > 0 && (
                            <div className="rounded-sm bg-white border mb-3">
                                <div className="divide-y divide-slate-200">
                                    <div className="min-h-[56px] min-w-[300px] max-w-2xl divide-y">
                                        {searchResults.data?.map((item, index) => {
                                            return (
                                                <>
                                                    <div
                                                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 min-h-[56px]"
                                                        key={index}
                                                        onClick={() => handleEditClick(item.id)}
                                                    >
                                                        <div className="flex justify-between w-full">
                                                            <div className="text-sm">{item.name}</div>
                                                            <div className="">
                                                                {item.published && (
                                                                    <span className="text-xs font-semibold uppercase rounded-lg bg-yellow-300 border border-yellow-400 px-2 py-0.5 text-yellow-800">
                                                                        Zadani
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span onClick={(e) => handleDeleteClick(e, item.id)} className="block w-10 h-10 flex items-center justify-center cursor-pointer bg-red-50 hover:bg-red-100 rounded-full">
                                                            <ReactSVG
                                                                src={"/media/icons/trash.svg"}
                                                                className="w-4 h-4 stroke-[#bd0000]"
                                                            />
                                                        </span>
                                                    </div>
                                                </>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
            </div>
            <div>
                        <Button
                            variant="outlined"
                            children={t("admin.setting.document.contract.rent.template.add.button")}
                            onClick={handleAddClick}
                            startIcon="/media/icons/plus.svg"
                        />
                    </div>
                </div>
            </div>
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                size="medium"
            >
                <ModalHeader
                    icon="/media/images/add-icon.svg"
                    title={
                        templateId
                            ? t("admin.setting.contract.rent.content.edit.title")
                            : t("admin.setting.contract.rent.content.add.title")
                    }
                    onHide={() => setShowEditModal(false)}
                />
                <ModalBody>
                    {showEditModal && (
                        <ContractRentTemplateEditCard
                            id={templateId}
                            onSuccess={handleTemplateEditSuccess}
                            onClose={() => setShowEditModal(false)}
                        />
                    )}

                </ModalBody>
            </Modal>
            <Dialog
                open={openDialog}
                title={t(
                    "admin.setting.document.contract.rent.template.dialog.delete.title"
                )}
                message={t(
                    "admin.setting.document.contract.rent.template.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.setting.document.contract.rent.template.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.setting.document.contract.rent.template.dialog.delete.canceltext"
                )}
                onConfirm={handleDeleteDialogConfirm}
                onCancel={handleDeleteDialogClose}
            />
        </>
    );
};
export default memo(ContractRentTemplateCard);
